import { CaretDownIcon, CaretUpIcon, CheckCircleIcon } from "/App.Components";
import "./DropdownButton.scss";
import createBem from "/Helpers/createBem";
import { DesignTheme } from "../../../App.GeneratedEnums";
import { ColorTheme } from "../../../Models/Models/ColorTheme";

export enum DropdownState {
  standard = 1,
  sublevel1 = 2,
  dropdownOpen = 3,
  dropdownClosed = 4,
  dropdownSublevel1Open = 5,
  dropdownSublevel1Closed = 6,
}

export interface Props {
  selected?: boolean;
  text: string;
  ariaLabel?: string;
  onClick: () => void;
  roundTopCorners?: boolean;
  roundBottomCorners?: boolean;
  dropdownState?: DropdownState;
  onArrowDown?: (currentlyFocused: HTMLButtonElement) => void;
  onArrowUp?: (currentlyFocused: HTMLButtonElement) => void;
  onEsc?: () => void;
  onTab?: () => void;
  onKeyboardSelect?: () => void;
  noTabStop?: boolean;
  hasSelected?: boolean;
  theme?: DesignTheme;
}

const DropdownButton = ({
  selected,
  text,
  ariaLabel,
  onClick,
  roundTopCorners = false,
  roundBottomCorners,
  dropdownState = DropdownState.standard,
  onArrowDown,
  onArrowUp,
  onEsc,
  onTab,
  onKeyboardSelect,
  noTabStop,
  hasSelected,
  theme,
}: Props) => {
  const bem = createBem("dropdown-button-norad");
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      tabIndex={noTabStop ? -1 : 0}
      onKeyDown={(e) => {
        if (e.code === "ArrowDown")
          onArrowDown && onArrowDown(e.target as unknown as HTMLButtonElement);
        else if (e.code === "ArrowUp")
          onArrowUp && onArrowUp(e.target as unknown as HTMLButtonElement);
        else if (e.code === "Escape") onEsc && onEsc();
        else if (e.code === "Tab") {
          onTab && onTab();
          return;
        } else if (
          onKeyboardSelect &&
          (e.code === "Enter" || e.code === "Space" || e.code === "NumpadEnter")
        ) {
          onKeyboardSelect();
        } else {
          return;
        }
        e.preventDefault();
      }}
      className={bem("", {
        "--has-selected": hasSelected,
        "--selected": selected,
        "--round-top-corners": roundTopCorners,
        "--round-bottom-corners": roundBottomCorners,
        "--sub-level-1": dropdownState === DropdownState.sublevel1,
        "--dropdown-standard":
          dropdownState === DropdownState.dropdownOpen ||
          dropdownState === DropdownState.dropdownClosed,
        "--dropdown-sublevel-1-open":
          dropdownState === DropdownState.dropdownSublevel1Open,
        "--theme-insight": theme === DesignTheme.Insight,
      })}
    >
      {(selected || hasSelected) && (
        <div className={bem("__icon-before")}>
          <CheckCircleIcon size="24" thickness="Reg" />
        </div>
      )}
      <span
        className={bem("__text", {
          "--indented":
            selected ||
            hasSelected ||
            dropdownState === DropdownState.sublevel1,
          "--dropdown":
            dropdownState === DropdownState.dropdownOpen ||
            dropdownState === DropdownState.dropdownClosed,
          "--dropdown-sublevel-1":
            dropdownState === DropdownState.dropdownSublevel1Open ||
            dropdownState === DropdownState.dropdownSublevel1Closed,
          "--theme-insight": theme === DesignTheme.Insight,
        })}
      >
        {text}
        {(dropdownState === DropdownState.dropdownOpen ||
          dropdownState === DropdownState.dropdownSublevel1Open) && (
          <span className={bem("__icon-after")}>
            <CaretUpIcon
              size="24"
              thickness="Reg"
              color={
                theme === DesignTheme.Insight
                  ? ColorTheme.insightDarkBlue
                  : undefined
              }
            />
          </span>
        )}
        {(dropdownState === DropdownState.dropdownClosed ||
          dropdownState === DropdownState.dropdownSublevel1Closed) && (
          <span className={bem("__icon-after")}>
            <CaretDownIcon
              size="24"
              thickness="Reg"
              color={
                theme === DesignTheme.Insight
                  ? ColorTheme.insightDarkBlue
                  : undefined
              }
            />
          </span>
        )}
      </span>
    </button>
  );
};

export default DropdownButton;
